/**
 * SkipLink — "Skip to main content" bypass block. TASK-028 P1-6 (WCAG 2.4.1).
 *
 * Rendered as the first focusable element so keyboard/SR users can jump past the
 * repeated header/nav to the page's main region. Visually hidden until focused
 * (sr-only → focus:not-sr-only).
 *
 * Targets `#main-content` (added to each page's <main>); falls back to the first
 * <main> element so it still works on pages that haven't been given the explicit
 * id yet. Moves focus programmatically — an href anchor alone only moves focus if
 * the target is focusable, which axe cannot verify (see PR-5 focus test).
 */
import type { MouseEvent } from 'react';

export function SkipLink(): JSX.Element {
  const focusMain = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const main =
      (document.getElementById('main-content') as HTMLElement | null) ??
      (document.querySelector('main') as HTMLElement | null);
    if (!main) return;
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus();
    if (typeof main.scrollIntoView === 'function') main.scrollIntoView();
  };

  return (
    <a
      href="#main-content"
      onClick={focusMain}
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-md focus:bg-evergreen focus:text-white focus:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-evergreen"
    >
      Skip to main content
    </a>
  );
}

export default SkipLink;
